import { useCallback, useMemo, useRef, useState } from "react"

interface HistoryState<T> {
  past: T[]
  present: T
  future: T[]
}

const HISTORY_LIMIT = 50

/**
 * State kèm hoàn tác / làm lại. Trình sửa menu giữ nguyên cả cây trong mỗi
 * bước nên chỉ cần snapshot tham chiếu — các hàm trong menu-tree đều trả cây mới.
 */
export const useHistoryState = <T,>(initial: T) => {
  const [state, setState] = useState<HistoryState<T>>({ past: [], present: initial, future: [] })
  const stateRef = useRef(state)
  stateRef.current = state

  const set = useCallback((updater: T | ((current: T) => T)) => {
    setState(current => {
      const next =
        typeof updater === "function" ? (updater as (value: T) => T)(current.present) : updater

      if (next === current.present) return current

      return {
        past: [...current.past, current.present].slice(-HISTORY_LIMIT),
        present: next,
        future: [],
      }
    })
  }, [])

  /** Đặt lại state và xoá lịch sử — dùng khi nạp dữ liệu từ server. */
  const reset = useCallback((value: T) => {
    setState({ past: [], present: value, future: [] })
  }, [])

  const undo = useCallback(() => {
    setState(current => {
      if (current.past.length === 0) return current
      const previous = current.past[current.past.length - 1]
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
      }
    })
  }, [])

  const redo = useCallback(() => {
    setState(current => {
      if (current.future.length === 0) return current
      const [next, ...rest] = current.future
      return {
        past: [...current.past, current.present],
        present: next,
        future: rest,
      }
    })
  }, [])

  return useMemo(
    () => ({
      value: state.present,
      set,
      reset,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
    }),
    [redo, reset, set, state.future.length, state.past.length, state.present, undo],
  )
}
